import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authService } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import PasswordField from "../components/auth/PasswordField";
import "./ChangePassword.css";

/**
 * Şifre değiştirme. İki bağlamda çalışır:
 *  - normal: kullanıcı kendi isteğiyle gelir, "Vazgeç" ile dönebilir
 *  - ilk giriş: zorunlu; sunucu da bu kullanıcıyı diğer uçlardan kilitler
 *    (FirstLoginPasswordFilter), o yüzden çıkış yolu yalnızca şifre değiştirmek.
 */
const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isFirstLogin = (location.state as any)?.isFirstLogin || false;

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const validate = (): string | null => {
    if (!oldPassword) return "Mevcut şifrenizi giriniz.";
    if (!newPassword) return "Yeni şifrenizi giriniz.";
    if (newPassword.length < 6) return "Yeni şifre en az 6 karakter olmalıdır.";
    if (newPassword !== confirm) return "Yeni şifreler eşleşmiyor.";
    if (oldPassword === newPassword) return "Yeni şifre eskisiyle aynı olamaz.";
    return null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);

    setLoading(true);
    setError("");
    try {
      const res = await authService.changePassword(oldPassword, newPassword);

      // Sunucu tazelenmiş token döner: ilk giriş kilidi ("pwd" claim'i) düşer,
      // kullanıcı yeniden giriş yapmak zorunda kalmaz.
      if (res?.token) localStorage.setItem("token", res.token);

      setDone(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Şifre değiştirilemedi. Mevcut şifrenizi kontrol edin.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="cp-wrap">
        <div className="cp-card">
          <div className="cp-done">
            <span className="cp-done-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="m8 12 3 3 5-6" />
              </svg>
            </span>
            <h1>Şifreniz güncellendi</h1>
            <p>
              Bundan sonraki girişlerinizde yeni şifrenizi kullanın.
              {isFirstLogin && " Hesabınız artık tam yetkiyle açık."}
            </p>
            <button className="cp-btn primary" onClick={() => navigate("/")}>
              Panele Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-wrap">
      <div className="cp-card">
        <header className="cp-head">
          <span className="cp-head-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </span>
          <div>
            <h1>{isFirstLogin ? "Şifrenizi Belirleyin" : "Şifre Değiştir"}</h1>
            <p>
              {isFirstLogin
                ? "İlk girişinizde güvenlik için şifrenizi değiştirmeniz gerekiyor."
                : "Hesabınızın şifresini güncelleyin."}
            </p>
          </div>
        </header>

        {user?.email && (
          <div className="cp-account">
            <span className="cp-account-label">Hesap</span>
            <span className="cp-account-value">{user.email}</span>
          </div>
        )}

        {isFirstLogin && (
          <div className="cp-banner" role="note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
            <span>
              Şifrenizi değiştirene kadar diğer ekranlar kapalıdır.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="cp-form lg-form" noValidate>
          {error && (
            <div className="lg-error" role="alert">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <PasswordField
            id="oldPassword"
            label="Mevcut Şifre"
            value={oldPassword}
            onChange={(v) => { setOldPassword(v); setError(""); }}
            autoComplete="current-password"
            autoFocus
          />

          <div className="cp-divider" aria-hidden="true" />

          <PasswordField
            id="newPassword"
            label="Yeni Şifre"
            value={newPassword}
            onChange={(v) => { setNewPassword(v); setError(""); }}
            strength
            hint="En az 6 karakter"
          />

          <PasswordField
            id="confirmPassword"
            label="Yeni Şifre (Tekrar)"
            value={confirm}
            onChange={(v) => { setConfirm(v); setError(""); }}
          />

          <div className="cp-actions">
            {!isFirstLogin && (
              <button
                type="button"
                className="cp-btn"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Vazgeç
              </button>
            )}
            <button type="submit" className="cp-btn primary" disabled={loading}>
              {loading ? (
                <>
                  <span className="lg-spinner" aria-hidden="true"></span>
                  Kaydediliyor…
                </>
              ) : (
                "Şifreyi Güncelle"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
